import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changeAccess, fetchPeople, invitePerson, type AccessPerson, type Me } from '../api';
import { Badge, Empty, ErrorBox, Loading, RelativeTime, Section, Table } from '../components/ui';

/**
 * Quién entra al portal y con qué papel.
 *
 * Antes del 2026-08-27 esto se hacía editando `AUTH_ALLOWLIST` en un fichero del
 * servidor y reiniciando el servicio, así que en la práctica el portal tenía un usuario.
 * Que se pueda hacer desde aquí es la diferencia entre una herramienta personal y una de
 * equipo.
 *
 * Dos cosas que la pantalla deja claras porque el sistema las impide y conviene saber por
 * qué antes de intentarlo: nadie puede quitarse a sí mismo la administración, y no se
 * puede dejar el portal sin ningún administrador.
 */
export function AccessTab({ me }: { me: Me }): JSX.Element {
  const client = useQueryClient();
  const [login, setLogin] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState('member');

  const query = useQuery({ queryKey: ['people-access'], queryFn: fetchPeople });
  const refrescar = (): void => {
    void client.invalidateQueries({ queryKey: ['people-access'] });
  };

  const invitar = useMutation({
    mutationFn: () => invitePerson({ githubLogin: login, displayName: nombre, role: rol }),
    onSuccess: () => {
      setLogin('');
      setNombre('');
      refrescar();
    },
  });
  const cambiar = useMutation({
    mutationFn: (input: { id: string; role?: string; canLogin?: boolean; notifyEmail?: boolean }) =>
      changeAccess(input.id, {
        role: input.role,
        canLogin: input.canLogin,
        notifyEmail: input.notifyEmail,
      }),
    onSuccess: refrescar,
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const { people, roles } = query.data;
  const conAcceso = people.filter((p) => p.can_login);
  const sinAcceso = people.filter((p) => !p.can_login);
  const esAdmin = me.role === 'admin';

  return (
    <div className="space-y-6">
      <Section
        title="Con acceso"
        subtitle={`${conAcceso.length} persona(s) pueden entrar al portal`}
      >
        {conAcceso.length === 0 ? (
          <Empty>Nadie. Algo va mal: revisa AUTH_ADMINS en el fichero de secretos.</Empty>
        ) : (
          <Table head={['Persona', 'GitHub', 'Papel', 'Avisos', 'Tareas', 'Última entrada', '']}>
            {conAcceso.map((p) => (
              <Fila
                key={p.id}
                persona={p}
                roles={roles}
                esAdmin={esAdmin}
                esUnoMismo={(p.github_login ?? '').toLowerCase() === me.login.toLowerCase()}
                ocupado={cambiar.isPending}
                onRol={(valor) => cambiar.mutate({ id: p.id, role: valor })}
                onAvisos={(valor) => cambiar.mutate({ id: p.id, notifyEmail: valor })}
                onQuitar={() => cambiar.mutate({ id: p.id, canLogin: false })}
              />
            ))}
          </Table>
        )}
        {cambiar.isError && <ErrorBox error={cambiar.error} />}
      </Section>

      {esAdmin && (
        <Section title="Dar acceso" subtitle="Entra con su cuenta de GitHub, como tú">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (login.trim()) invitar.mutate();
            }}
          >
            <label className="text-sm">
              <span className="mb-1 block text-slate-500">Usuario de GitHub</span>
              <input
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="usuario"
                className="rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-900"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-500">Nombre (opcional)</span>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Cómo se le llama"
                className="rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-900"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-500">Papel</span>
              <select
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                className="rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-900"
              >
                {roles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={!login.trim() || invitar.isPending}
              className="rounded bg-slate-800 px-4 py-2 text-sm text-white disabled:opacity-40 dark:bg-slate-200 dark:text-slate-900"
            >
              {invitar.isPending ? 'Dando acceso…' : 'Dar acceso'}
            </button>
          </form>
          {invitar.isError && <ErrorBox error={invitar.error} />}
          <p className="mt-3 text-xs text-slate-500">
            <strong>Administrador</strong> gestiona personas y configuración.{' '}
            <strong>Colaborador</strong> comenta, pide funciones y lleva las tareas que se le
            asignan. <strong>Observador</strong> solo lee.
          </p>
        </Section>
      )}

      <Section
        title="Sin acceso"
        subtitle="Figuran por haber firmado commits. No pueden entrar hasta que se les dé acceso."
      >
        {sinAcceso.length === 0 ? (
          <Empty>Nadie más en el sistema.</Empty>
        ) : (
          <Table head={['Persona', 'GitHub', 'Tareas abiertas', '']}>
            {sinAcceso.map((p) => (
              <tr key={p.id} className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-3 py-2 text-sm">{p.display_name}</td>
                <td className="px-3 py-2 text-sm text-slate-500">
                  {p.github_login ? `@${p.github_login}` : '—'}
                </td>
                <td className="px-3 py-2 text-sm">{p.tareas_abiertas}</td>
                <td className="px-3 py-2 text-right">
                  {esAdmin && p.github_login && (
                    <button
                      onClick={() => cambiar.mutate({ id: p.id, role: 'member', canLogin: true })}
                      disabled={cambiar.isPending}
                      className="text-sm text-slate-500 hover:underline"
                    >
                      Dar acceso como colaborador
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

function Fila({
  persona: p,
  roles,
  esAdmin,
  esUnoMismo,
  ocupado,
  onRol,
  onAvisos,
  onQuitar,
}: {
  persona: AccessPerson;
  roles: { value: string; label: string }[];
  esAdmin: boolean;
  esUnoMismo: boolean;
  ocupado: boolean;
  onRol: (valor: string) => void;
  onAvisos: (valor: boolean) => void;
  onQuitar: () => void;
}): JSX.Element {
  return (
    <tr className="border-t border-slate-200 dark:border-slate-700">
      <td className="px-3 py-2 text-sm">
        {p.display_name}
        {esUnoMismo && <span className="ml-2 text-xs text-slate-400">(tú)</span>}
      </td>
      <td className="px-3 py-2 text-sm text-slate-500">
        {p.github_login ? `@${p.github_login}` : '—'}
      </td>
      <td className="px-3 py-2 text-sm">
        {esAdmin && !esUnoMismo ? (
          <select
            value={p.auth_role ?? 'observer'}
            onChange={(e) => onRol(e.target.value)}
            disabled={ocupado}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
          >
            {roles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        ) : (
          <Badge tone={p.auth_role === 'admin' ? 'good' : 'neutral'}>
            {roles.find((r) => r.value === p.auth_role)?.label ?? '—'}
          </Badge>
        )}
      </td>
      <td className="px-3 py-2 text-sm">
        {/* Cada quien decide si quiere sus propios avisos; un administrador puede
            apagarlos en cualquiera. El correo de asignación remite aquí, así que esta
            casilla tiene que existir o el correo estaría prometiendo algo que no hay. */}
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={p.notify_email}
            disabled={ocupado || (!esAdmin && !esUnoMismo)}
            onChange={(e) => onAvisos(e.target.checked)}
          />
          <span className="text-slate-500">
            {p.email ? 'por correo' : 'sin correo en su ficha'}
          </span>
        </label>
      </td>
      <td className="px-3 py-2 text-sm">{p.tareas_abiertas}</td>
      <td className="px-3 py-2 text-sm text-slate-500">
        <RelativeTime iso={p.last_login_at} />
      </td>
      <td className="px-3 py-2 text-right">
        {esAdmin && !esUnoMismo && (
          <button
            onClick={onQuitar}
            disabled={ocupado}
            className="text-sm text-slate-500 hover:underline"
          >
            Quitar acceso
          </button>
        )}
        {esUnoMismo && (
          <span className="text-xs text-slate-400">No puedes cambiarte a ti mismo</span>
        )}
      </td>
    </tr>
  );
}
